import { gql } from '@apollo/client';
import graphQLRequester from '../../request/GraphQLRequester';
import TimeZone from '../../request/Timezone';

/**
 * Face-ID — Visão geral (Overview tab) + Motoristas (driver registration status) data source.
 *
 * Field names mirror the Janus contract (BFF PR #992). Each overview KPI is its own independent
 * root query, fetched as a separate GraphQL operation so every card loads (and fails) on its own —
 * a slow or failing KPI never blocks the others.
 *
 * `vehiclesRunningWithoutDriverCount` and the coverage chart additionally carry the `timezone`
 * argument (it shifts the day boundary); the count KPIs do not.
 */

export type FaceIdCoverageMetadata = {
  fromTimestamp: number;
  toTimestamp: number;
  validEventsTotal: number;
  identifiedEventsTotal: number;
  averageCoveragePercentage: number;
};

export type FaceIdCoveragePoint = {
  bucketStart: number;
  validEvents: number;
  identifiedEvents: number;
  coveragePercentage: number;
};

export type FaceIdCoverageChart = {
  metadata: FaceIdCoverageMetadata;
  data: FaceIdCoveragePoint[];
};

/**
 * Bucket size for the coverage series. V1 is fixed to DAILY at the BFF (no `granularity`
 * argument); the remaining values are reserved for the chart's toggle UI until the backend
 * supports server-side bucketing.
 */
export type FaceIdCoverageGranularity = 'DAILY' | 'WEEKLY' | 'MONTHLY';

/** Overall identification-method health state of the fleet (Janus `fleetIdentificationHealth`, BFF PR #1012). */
export type FleetIdentificationHealthState = 'HEALTHY' | 'WARNING' | 'CRITICAL';

/**
 * Identification-method health of the requester's fleet. The BFF derives `state` from a weighted score
 * of M1 (complete registration) and M2 (identified events), so the FE consumes it directly instead of
 * classifying the registration percentage itself. The fleet comes from the session — no arguments.
 */
export type FleetIdentificationHealth = {
  state: FleetIdentificationHealthState;
  /** M1 — share (0–100) of fleet drivers with a Cobli Cam photo processed into an embedding. */
  completeRegistrationRate: number;
  /** M2 — share (0–100) of valid video events identified with a driver over the last 14 days. */
  identifiedEventsRate: number;
  /** RFC 3339 timestamp of when the metric was computed. */
  calculatedAt: string;
};

/** Raw BFF payload (snake_case, mirrors `FleetIdentificationHealthDTO`) before FE camelCase mapping. */
type RawFleetIdentificationHealth = {
  state: FleetIdentificationHealthState;
  complete_registration_rate: number;
  identified_events_rate: number;
  calculated_at: string;
};

export type FaceIdRegistrationStatus = 'COMPLETE' | 'INCOMPLETE';

export type DriverFaceIdRegistration = {
  driverId: string;
  status: FaceIdRegistrationStatus;
  sampleImageCount: number;
  /** RFC 3339 timestamp of the last sample image, or null when INCOMPLETE. */
  lastSampleImageAt: string | null;
};

/** Shared params for the group-scoped count KPIs (one per separate operation). */
export type FaceIdKpiParams = {
  groupIds?: string[];
};

export type GetVehiclesRunningWithoutDriverCountParams = {
  groupIds?: string[];
};

export type GetFaceIdCoverageChartParams = {
  /** epoch ms */
  fromTimestamp: number;
  /** epoch ms */
  toTimestamp: number;
  groupIds?: string[];
};

export type GetDriverFaceIdRegistrationsParams = {
  driverIds: string[];
};

export class GraphQLFaceIdOverviewRequester {
  // `graphQLRequester.query` resolves to `{ errors, ...data }` (errorPolicy 'all'), so partial data can
  // arrive alongside errors. Reject on any GraphQL error instead of returning a partial/invalid payload.
  private assertNoErrors = (errors?: unknown[]) => {
    if (errors?.length) {
      throw { errors };
    }
  };

  // One operation per KPI — each card fetches independently, so a slow/failing KPI never blocks the rest.
  // None of these carry `timezone` (only the vehicles count and the coverage chart need the day boundary).
  private queryDriversCount = gql`
    query driversCount($groupIds: [String]) {
      driversCount(groupIds: $groupIds)
    }
  `;

  private queryDriversWithCompleteFaceIdRegistrationCount = gql`
    query driversWithCompleteFaceIdRegistrationCount($groupIds: [String]) {
      driversWithCompleteFaceIdRegistrationCount(groupIds: $groupIds)
    }
  `;

  private queryUnnamedFaceClustersCount = gql`
    query unnamedFaceClustersCount($groupIds: [String]) {
      unnamedFaceClustersCount(groupIds: $groupIds)
    }
  `;

  private queryHasFaceIdIdentificationHistory = gql`
    query hasFaceIdIdentificationHistory($groupIds: [String]) {
      hasFaceIdIdentificationHistory(groupIds: $groupIds)
    }
  `;

  // Split from the overview: this KPI is the slow one upstream and `timezone` shifts the day boundary,
  // so it carries its own operation and lets the four fast cards render without waiting for it.
  private queryVehiclesRunningWithoutDriverCount = gql`
    query vehiclesRunningWithoutDriverCount($groupIds: [String], $timezone: String!) {
      vehiclesRunningWithoutDriverCount(groupIds: $groupIds, timezone: $timezone)
    }
  `;

  private queryFaceIdCoverageChart = gql`
    query faceIdCoverageChart($fromTimestamp: Float!, $toTimestamp: Float!, $timezone: String!, $groupIds: [String]) {
      faceIdCoverageChart(fromTimestamp: $fromTimestamp, toTimestamp: $toTimestamp, timezone: $timezone, groupIds: $groupIds) {
        metadata {
          fromTimestamp
          toTimestamp
          validEventsTotal
          identifiedEventsTotal
          averageCoveragePercentage
        }
        data {
          bucketStart
          validEvents
          identifiedEvents
          coveragePercentage
        }
      }
    }
  `;

  // Fleet-scoped (fleet resolved from the session), so it carries no arguments — unlike the group-scoped
  // count KPIs above.
  private queryFleetIdentificationHealth = gql`
    query fleetIdentificationHealth {
      fleetIdentificationHealth {
        state
        complete_registration_rate
        identified_events_rate
        calculated_at
      }
    }
  `;

  private queryDriverFaceIdRegistrations = gql`
    query driverFaceIdRegistrations($driverIds: [ID!]!) {
      driverFaceIdRegistrations(driverIds: $driverIds) {
        driverId
        status
        sampleImageCount
        lastSampleImageAt
      }
    }
  `;

  /** Total active drivers in the fleet (denominator of the registration KPI). */
  public getDriversCount = ({ groupIds }: FaceIdKpiParams): Promise<number> =>
    graphQLRequester
      .query<{ driversCount: number; errors?: unknown[] }>({
        query: this.queryDriversCount,
        variables: { groupIds },
      })
      .then(({ errors, driversCount }) => {
        this.assertNoErrors(errors);
        return driversCount;
      });

  /** Drivers with a complete Face-ID registration (≥1 face_image with recognition embedding). */
  public getDriversWithCompleteFaceIdRegistrationCount = ({ groupIds }: FaceIdKpiParams): Promise<number> =>
    graphQLRequester
      .query<{ driversWithCompleteFaceIdRegistrationCount: number; errors?: unknown[] }>({
        query: this.queryDriversWithCompleteFaceIdRegistrationCount,
        variables: { groupIds },
      })
      .then(({ errors, driversWithCompleteFaceIdRegistrationCount }) => {
        this.assertNoErrors(errors);
        return driversWithCompleteFaceIdRegistrationCount;
      });

  /** Unnamed face clusters with a capture in the last 14 days. */
  public getUnnamedFaceClustersCount = ({ groupIds }: FaceIdKpiParams): Promise<number> =>
    graphQLRequester
      .query<{ unnamedFaceClustersCount: number; errors?: unknown[] }>({
        query: this.queryUnnamedFaceClustersCount,
        variables: { groupIds },
      })
      .then(({ errors, unnamedFaceClustersCount }) => {
        this.assertNoErrors(errors);
        return unnamedFaceClustersCount;
      });

  /** Drives the FE state machine: Onboarding vs Normal/All-resolved. */
  public getHasFaceIdIdentificationHistory = ({ groupIds }: FaceIdKpiParams): Promise<boolean> =>
    graphQLRequester
      .query<{ hasFaceIdIdentificationHistory: boolean; errors?: unknown[] }>({
        query: this.queryHasFaceIdIdentificationHistory,
        variables: { groupIds },
      })
      .then(({ errors, hasFaceIdIdentificationHistory }) => {
        this.assertNoErrors(errors);
        return hasFaceIdIdentificationHistory;
      });

  public getVehiclesRunningWithoutDriverCount = ({ groupIds }: GetVehiclesRunningWithoutDriverCountParams): Promise<number> =>
    graphQLRequester
      .query<{ vehiclesRunningWithoutDriverCount: number; errors?: unknown[] }>({
        query: this.queryVehiclesRunningWithoutDriverCount,
        variables: { groupIds, timezone: TimeZone.getTimezone() },
      })
      .then(({ errors, vehiclesRunningWithoutDriverCount }) => {
        this.assertNoErrors(errors);
        return vehiclesRunningWithoutDriverCount;
      });

  public getFaceIdCoverageChart = ({ fromTimestamp, toTimestamp, groupIds }: GetFaceIdCoverageChartParams): Promise<FaceIdCoverageChart> =>
    graphQLRequester
      .query<{ faceIdCoverageChart: FaceIdCoverageChart; errors?: unknown[] }>({
        query: this.queryFaceIdCoverageChart,
        variables: { fromTimestamp, toTimestamp, timezone: TimeZone.getTimezone(), groupIds },
      })
      .then((response) => {
        this.assertNoErrors(response.errors);
        return response.faceIdCoverageChart;
      });

  /** Fleet identification-method health, used to drive the "saúde do método" tag. */
  public getFleetIdentificationHealth = (): Promise<FleetIdentificationHealth | null> =>
    graphQLRequester
      .query<{ fleetIdentificationHealth: RawFleetIdentificationHealth | null; errors?: unknown[] }>({
        query: this.queryFleetIdentificationHealth,
      })
      .then(({ errors, fleetIdentificationHealth }) => {
        this.assertNoErrors(errors);
        if (!fleetIdentificationHealth) return null;
        return {
          state: fleetIdentificationHealth.state,
          completeRegistrationRate: fleetIdentificationHealth.complete_registration_rate,
          identifiedEventsRate: fleetIdentificationHealth.identified_events_rate,
          calculatedAt: fleetIdentificationHealth.calculated_at,
        };
      });

  public getDriverFaceIdRegistrations = ({ driverIds }: GetDriverFaceIdRegistrationsParams): Promise<DriverFaceIdRegistration[]> =>
    graphQLRequester
      .query<{ driverFaceIdRegistrations: DriverFaceIdRegistration[]; errors?: unknown[] }>({
        query: this.queryDriverFaceIdRegistrations,
        variables: { driverIds },
      })
      .then((response) => {
        this.assertNoErrors(response.errors);
        return response.driverFaceIdRegistrations;
      });
}

export default GraphQLFaceIdOverviewRequester;
